
import React, { useMemo } from 'react';
import { Info, CheckCircle, Minus } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

interface Filing {
  return_type: string;
  date_of_filing: string;
  financial_year: string;
  tax_period: string;
  status: string;
}

interface ComplianceCalendarProps {
  filings: Filing[];
  returnType: string;
  gstin?: string;
  state?: string;
  companyName?: string;
}

export const ComplianceCalendar: React.FC<ComplianceCalendarProps> = ({ 
  filings, 
  returnType, 
  gstin = '', 
  state = '', 
  companyName = '' 
}) => {
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const getDueDate = (taxPeriod: string, financialYear: string, returnType: string): Date => {
    const monthMap: { [key: string]: number } = {
      'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
      'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
    };
    
    const month = monthMap[taxPeriod];
    if (month === undefined) return new Date();
    
    const [startYear] = financialYear.split('-').map(y => parseInt(y));
    if (isNaN(startYear)) return new Date();
    
    // For financial year 2023-24, April 2023 to March 2024
    const adjustedYear = month >= 3 ? startYear : startYear + 1;
    
    const dueDay = returnType === 'GSTR1' ? 11 : 20;
    return new Date(adjustedYear, month + 1, dueDay);
  };

  const getDelayDays = (filing: Filing): number => {
    const filingDate = new Date(filing.date_of_filing);
    const dueDate = getDueDate(filing.tax_period, filing.financial_year, filing.return_type);
    
    if (isNaN(filingDate.getTime()) || isNaN(dueDate.getTime())) return 0;
    if (filingDate <= dueDate) return 0;
    
    const diffTime = filingDate.getTime() - dueDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getFinancialYears = useMemo(() => {
    const years = new Set(filings.map(filing => filing.financial_year));
    // Sort years with latest first, then slice to show last 5 years
    return Array.from(years).sort((a, b) => b.localeCompare(a)).slice(0, 5);
  }, [filings]);

  const getFilingForPeriod = (month: string, year: string) => {
    const monthMap: { [key: string]: string } = {
      'Jan': 'January', 'Feb': 'February', 'Mar': 'March', 'Apr': 'April', 
      'May': 'May', 'Jun': 'June', 'Jul': 'July', 'Aug': 'August', 
      'Sep': 'September', 'Oct': 'October', 'Nov': 'November', 'Dec': 'December'
    };
    
    return filings.find(filing => 
      filing.tax_period === monthMap[month] && filing.financial_year === year
    );
  };

  const renderCell = useMemo(() => (month: string, year: string) => {
    const filing = getFilingForPeriod(month, year);
    
    if (!filing) {
      return (
        <div className="flex justify-center items-center h-8 w-full">
          <Minus className="w-3 h-3 text-gray-400" />
        </div>
      );
    }

    const delayDays = getDelayDays(filing);
    const isOnTime = delayDays === 0;
    const dueDate = getDueDate(filing.tax_period, filing.financial_year, filing.return_type);
    const filingDate = new Date(filing.date_of_filing);

    const tooltipContent = (
      <div className="space-y-1">
        <div className="font-semibold">{gstin}</div>
        <div className="text-xs text-gray-300">{state} • {companyName}</div>
        <div className="text-xs">
          <div>Due: {dueDate.toLocaleDateString("en-GB")}</div>
          <div>Filed: {filingDate.toLocaleDateString("en-GB")}</div>
          {!isOnTime && <div className="text-red-300">Delay: {delayDays} days</div>}
        </div>
      </div>
    );

    if (isOnTime) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex justify-center items-center h-8 w-full cursor-pointer">
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className='bg-white'>
              {tooltipContent}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    } else {
      const bgColor = delayDays >= 30 ? 'bg-red-500' : 
                     delayDays >= 15 ? 'bg-orange-500' : 'bg-yellow-500';
      
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex justify-center items-center h-8 w-full cursor-pointer">
                <div className={`w-5 h-5 rounded-full ${bgColor} flex items-center justify-center`}>
                  <span className="text-white text-xs font-semibold">{delayDays}</span>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className='bg-white'>
              {tooltipContent}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
  }, [filings, gstin, state, companyName, returnType]);

  const financialYears = getFinancialYears;
  
  // Calculate stats without risk-weighted score
  const stats = useMemo(() => {
    const totalFilings = filings.length;
    const onTimeFilings = filings.filter(filing => getDelayDays(filing) === 0).length;
    const onTimePercentage = totalFilings > 0 ? Math.round((onTimeFilings / totalFilings) * 100) : 0;
    
    const delayedFilings = filings.filter(filing => getDelayDays(filing) > 0);
    const totalDelays = delayedFilings.reduce((sum, filing) => sum + getDelayDays(filing), 0);
    const avgDelay = delayedFilings.length > 0 ? totalDelays / delayedFilings.length : 0;
    
    return {
      onTimePercentage,
      totalDelays,
      avgDelay
    };
  }, [filings]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">
            {returnType === 'GSTR1' ? 'GSTR-1' : 'GSTR-3B'} Filing Status
          </h3>
          <Info className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* Updated Stats - Removed Risk-Weighted Score */}
      <div className="grid grid-cols-3 gap-4 mb-3">
        <div>
          <div className="text-xl font-bold text-gray-900">{stats.onTimePercentage}%</div>
          <div className="text-xs text-gray-500">On-Time Rate</div>
        </div>
        <div>
          <div className="text-xl font-bold text-red-600">{stats.totalDelays}</div>
          <div className="text-xs text-gray-500">Total Delays</div>
        </div>
        <div>
          <div className="text-xl font-bold text-orange-600">{stats.avgDelay.toFixed(1)}</div>
          <div className="text-xs text-gray-500">Avg Delay</div>
        </div>
      </div>

      {/* Enhanced Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs flex-wrap">
        <div className="flex items-center gap-1">
          <Minus className="w-3 h-3 text-gray-400" />
          <span className="text-gray-600">No Data</span>
        </div>
        <div className="flex items-center gap-1">
          <CheckCircle className="w-3 h-3 text-green-500" />
          <span className="text-gray-600">On-Time</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-yellow-500 flex items-center justify-center">
            <span className="text-white text-[8px]">1</span>
          </div>
          <span className="text-gray-600">1-14 days</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-orange-500 flex items-center justify-center">
            <span className="text-white text-[8px]">15</span>
          </div>
          <span className="text-gray-600">15-29 days</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-red-500 flex items-center justify-center">
            <span className="text-white text-[8px]">30</span>
          </div>
          <span className="text-gray-600">30+ days</span>
        </div>
      </div>

      {/* Calendar Grid - Years now sorted with latest first */}
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: '600px' }}>
          <thead>
            <tr>
              <th className="text-left p-1 border-b border-gray-200 w-12 text-xs font-medium text-gray-600">Year</th>
              {months.map((month) => (
                <th key={month} className="text-center p-1 border-b border-gray-200 text-xs font-medium text-gray-600" style={{ width: '40px' }}>
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {financialYears.map((year) => (
              <tr key={year} className="border-b border-gray-100 last:border-b-0">
                <td className="p-1 text-left">
                  <span className="text-xs font-semibold text-gray-900">
                    {year.split('-')[0]}
                  </span>
                </td>
                {months.map((month) => (
                  <td key={`${year}-${month}`} className="p-1 text-center border-l border-gray-100 first:border-l-0" style={{ width: '40px' }}>
                    {renderCell(month, year)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>        
        </table>
      </div>
    </div>
  );
};
